import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { keycloak } from '../lib/keycloak';
import { clearLegacyTokenStorage, setAccessToken } from '../lib/token';
import { useAuthStore } from '../store/authStore';

interface AuthContextValue {
  initialized: boolean;
}

const AuthContext = createContext<AuthContextValue>({ initialized: false });

export function useAuthInitialized() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { fetchUserProfile, clearAuth } = useAuthStore();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    clearLegacyTokenStorage();
    keycloak
      .init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        silentCheckSsoFallback: false,
        redirectUri: `${window.location.origin}/`,
      })
      .then(async (authenticated) => {
        if (authenticated && keycloak.token) {
          setAccessToken(keycloak.token);
          await fetchUserProfile();
        } else {
          setAccessToken(null);
          clearAuth();
        }
      })
      .catch(() => {
        setAccessToken(null);
        clearAuth();
      })
      .finally(() => {
        setInitialized(true);
      });

    const updateToken = async () => {
      try {
        await keycloak.updateToken(30);
        if (keycloak.token) {
          setAccessToken(keycloak.token);
          if (useAuthStore.getState().isAuthenticated) {
            useAuthStore.setState({ token: keycloak.token });
          }
        }
      } catch {
        setAccessToken(null);
        clearAuth();
      }
    };

    keycloak.onTokenExpired = updateToken;
  }, [fetchUserProfile, clearAuth]);

  return <AuthContext.Provider value={{ initialized }}>{children}</AuthContext.Provider>;
}
